// Bestandsauswertungen: Bestand und Wert nach Standort und Warengruppe mit Reichweite und Umschlag, Ladenhüter,
// ablaufende Chargen, Schwund. Mengen sieht, wer die Auswertung lesen darf; Werte nur mit `costs.read`.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { SHRINKAGE_GROUPINGS, STOCK_GROUPINGS } from '../lib/labels'
import { compareField, costFields, csvField, locationFields, periodFields, READ_STOCK, reportScope, zCsv, zDataState, zDay, zIdOrNone, zPeriod } from './common'

export const stockReport = defineContract({
  name: 'reporting.stock.report',
  title: 'Bestand auswerten',
  description: 'Bestand heute nach Standort, Warengruppe oder Artikel – mit Reichweite, Lagerumschlag und, mit dem Recht dazu, dem Lagerwert.',
  llm: {
    description:
      'Wertet den Bestand von heute aus, gruppiert nach `groupBy` (location, itemGroup, item): Menge, Lagerwert zum gleitenden Durchschnitt (Cent, nur mit dem Recht auf Einstandspreise), ' +
      'Reichweite in Tagen und Lagerumschlag aufs Jahr (in Hundertsteln: 1250 = 12,5-mal), gerechnet aus dem Absatz der letzten `rangeDays` Tage. ' +
      'Verwenden für „Wie hoch ist der Lagerwert je Standort?“, „Wie lange reicht der Bestand?“. ' +
      'Nicht für den Bestand eines einzelnen Artikels (inventory.stock.item), nicht für Artikel ohne Bewegung (reporting.stock.deadStock), nicht für Ware kurz vor dem Verfallsdatum (reporting.stock.expiry).',
    keywords: ['Lagerwert', 'Reichweite', 'Lagerumschlag', 'Bestand je Standort', 'Bestandswert'],
    examples: [{ groupBy: 'location' }],
  },
  input: z.strictObject({
    ...locationFields,
    itemGroupId: zIdOrNone.optional(),
    groupBy: z.enum(STOCK_GROUPINGS, { error: 'Bitte eine Gruppierung aus der Liste wählen.' }).default('itemGroup'),
    /** Tage, aus deren Absatz Reichweite und Umschlag gerechnet werden */
    rangeDays: z.int().min(7, { error: 'Bitte mindestens 7 Tage wählen.' }).max(365, { error: 'Höchstens 365 Tage.' }).default(30),
    limit: z.int().min(1).max(500).default(200),
    ...csvField,
  }),
  output: z.object({
    asOf: zDay,
    rangeDays: z.int(),
    groupBy: z.enum(STOCK_GROUPINGS),
    groupByLabel: z.string(),
    totals: z.object({
      /** Artikel mit Bestand */
      itemCount: z.int(),
      valueCents: z.int().optional(),
      /** Wareneinsatz der letzten `rangeDays` Tage */
      costOfSalesCents: z.int().optional(),
      /** Lagerwert zu Beginn des Zeitraums – der Durchschnitt aus Anfang und heute ist die Basis des Umschlags */
      openingValueCents: z.int().optional(),
      daysOfSupply: z.int().nullable(),
      turnoverRate: z.int().nullable(),
      /** Artikel-Standorte unter ihrem Meldebestand */
      belowReorderCount: z.int(),
    }),
    rows: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        sublabel: z.string().nullable(),
        itemCount: z.int(),
        /** Bestand in Tausendstel der Basiseinheit; null, wenn Artikel mit verschiedenen Einheiten zusammenkommen */
        onHandQty: z.int().nullable(),
        unit: z.string().nullable(),
        valueCents: z.int().optional(),
        /** Anteil am Lagerwert in Basispunkten */
        shareBp: z.int().optional(),
        /** Abgang der letzten `rangeDays` Tage (Verkauf abzüglich Rücknahmen) */
        issuedQty: z.int().nullable(),
        daysOfSupply: z.int().nullable(),
        turnoverRate: z.int().nullable(),
        belowReorderCount: z.int(),
      }),
    ),
    truncated: z.boolean(),
    /** Lagerwert je Tag der letzten `rangeDays` Tage (nur mit `costs.read`) */
    valueSeries: z.array(z.object({ date: zDay, valueCents: z.int() })).optional(),
    data: zDataState,
    csv: zCsv.optional(),
  }),
  permission: READ_STOCK,
  risk: 'read',
  scope: reportScope,
  fieldPermissions: { ...costFields(['totals'], ['valueCents', 'costOfSalesCents', 'openingValueCents']), ...costFields(['rows.*'], ['valueCents', 'shareBp']), ...costFields([''], ['valueSeries']) },
})

export const stockDeadStock = defineContract({
  name: 'reporting.stock.deadStock',
  title: 'Ladenhüter anzeigen',
  description: 'Artikel, die an einem Standort liegen und sich seit einer Zahl von Tagen nicht bewegt haben.',
  llm: {
    description:
      'Listet Artikel mit Bestand, die sich an ihrem Standort seit mindestens `days` Tagen nicht bewegt haben (kein Verkauf, kein Zugang, keine Umlagerung, keine Korrektur) – älteste zuerst, mit Bestand und, mit dem Recht dazu, gebundenem Wert in Cent. ' +
      'Verwenden für Fragen wie „Welche Artikel liegen seit 60 Tagen?“. Mengen in Tausendstel der Basiseinheit. ' +
      'Nicht für Artikel mit wenig Absatz (reporting.sales.movers mit view: slow).',
    keywords: ['Ladenhüter', 'liegt seit', 'ohne Bewegung', 'Altbestand', 'totes Kapital'],
    examples: [{ days: 60 }],
  },
  input: z.strictObject({
    days: z.int().min(1, { error: 'Bitte mindestens einen Tag angeben.' }).max(730, { error: 'Höchstens 730 Tage.' }).default(60),
    ...locationFields,
    itemGroupId: zIdOrNone.optional(),
    limit: z.int().min(1).max(500).default(100),
    ...csvField,
  }),
  output: z.object({
    asOf: zDay,
    days: z.int(),
    totals: z.object({ rowCount: z.int(), itemCount: z.int(), valueCents: z.int().optional() }),
    rows: z.array(
      z.object({
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        groupName: z.string().nullable(),
        location: z.object({ id: zId, code: z.string(), name: z.string() }),
        onHandQty: z.int(),
        unit: z.string(),
        valueCents: z.int().optional(),
        /** Tag der letzten Bewegung am Standort */
        lastMovementDate: zDay,
        daysWithoutMovement: z.int(),
        /** letzter Verkauf am Standort; null = dort noch nie verkauft */
        lastSaleDate: zDay.nullable(),
      }),
    ),
    truncated: z.boolean(),
    csv: zCsv.optional(),
  }),
  permission: READ_STOCK,
  risk: 'read',
  scope: reportScope,
  fieldPermissions: costFields(['totals', 'rows.*'], ['valueCents']),
})

export const stockExpiry = defineContract({
  name: 'reporting.stock.expiry',
  title: 'Ablaufende Chargen bewerten',
  description: 'Chargen mit Bestand, deren Datum in den nächsten Tagen erreicht oder überschritten ist – mit dem Wert, der auf dem Spiel steht.',
  llm: {
    description:
      'Listet Chargen mit Bestand, deren Mindesthaltbarkeits- oder Verbrauchsdatum binnen `withinDays` Tagen erreicht wird oder schon überschritten ist, frühestes zuerst, mit Restmenge und – mit dem Recht auf Einstandspreise – dem gebundenen Wert in Cent, dazu Summen je Frist. ' +
      'Verwenden für „Was läuft demnächst ab und wie viel Wert hängt daran?“. Zum Sperren oder Abschreiben einer Charge dienen die Aktionen des Bestands (inventory.lot.*).',
    keywords: ['MHD', 'läuft ab', 'Verfall', 'Restlaufzeit', 'MHD-Risiko'],
    examples: [{ withinDays: 30 }],
  },
  input: z.strictObject({
    withinDays: z.int().min(0).max(365, { error: 'Höchstens 365 Tage.' }).default(30),
    ...locationFields,
    limit: z.int().min(1).max(500).default(100),
    ...csvField,
  }),
  output: z.object({
    asOf: zDay,
    withinDays: z.int(),
    totals: z.object({ lotCount: z.int(), expiredCount: z.int(), valueCents: z.int().optional(), expiredValueCents: z.int().optional() }),
    buckets: z.array(z.object({ key: z.string(), label: z.string(), lotCount: z.int(), valueCents: z.int().optional() })),
    rows: z.array(
      z.object({
        lotId: zId,
        lotNo: z.string(),
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        location: z.object({ id: zId, code: z.string(), name: z.string() }),
        bestBefore: zDay,
        /** Tage bis zum Datum; negativ = überschritten */
        daysLeft: z.int(),
        onHandQty: z.int(),
        unit: z.string(),
        valueCents: z.int().optional(),
        blocked: z.boolean(),
      }),
    ),
    truncated: z.boolean(),
    csv: zCsv.optional(),
  }),
  permission: READ_STOCK,
  // ohne Chargenführung gibt es nichts, was abläuft – Aktion und Reiter verschwinden mit dem Schalter
  feature: 'lots',
  risk: 'read',
  scope: reportScope,
  fieldPermissions: { ...costFields(['totals'], ['valueCents', 'expiredValueCents']), ...costFields(['buckets.*', 'rows.*'], ['valueCents']) },
})

export const shrinkageReport = defineContract({
  name: 'reporting.shrinkage.report',
  title: 'Schwund auswerten',
  description: 'Korrekturbuchungen und gebuchte Inventurdifferenzen eines Zeitraums nach Grund, Standort, Monat oder Artikel.',
  llm: {
    description:
      'Wertet Bestandskorrekturen (Bruch, Verderb, Eigenverbrauch, Fund …) und gebuchte Inventurdifferenzen eines Zeitraums aus, gruppiert nach `groupBy` (reason, location, month, item): Zahl der Buchungen, Menge und – mit dem Recht auf Einstandspreise – Wert in Cent (Verlust negativ, Fund positiv). ' +
      'Verwenden für „Wie viel Bruch hatten wir im letzten Monat?“, „Wo entsteht der meiste Schwund?“. Nicht für Kassendifferenzen (pos.shift.list).',
    keywords: ['Schwund', 'Bruch', 'Verderb', 'Inventurdifferenz', 'Korrekturbuchungen', 'Verlust'],
    examples: [{ period: 'last30', groupBy: 'reason' }],
  },
  input: z.strictObject({
    ...periodFields,
    ...compareField,
    ...locationFields,
    groupBy: z.enum(SHRINKAGE_GROUPINGS, { error: 'Bitte eine Gruppierung aus der Liste wählen.' }).default('reason'),
    limit: z.int().min(1).max(500).default(100),
    ...csvField,
  }),
  output: z.object({
    period: zPeriod,
    comparison: zPeriod.nullable(),
    comparisonLabel: z.string().nullable(),
    groupBy: z.enum(SHRINKAGE_GROUPINGS),
    groupByLabel: z.string(),
    totals: z.object({
      movementCount: z.int(),
      /** Wert der Verluste (negativ) */
      lossCents: z.int().optional(),
      /** Wert der Funde und Mehrbestände (positiv) */
      gainCents: z.int().optional(),
      netCents: z.int().optional(),
      comparisonNetCents: z.int().nullable().optional(),
    }),
    sources: z.array(z.object({ source: z.string(), label: z.string(), movementCount: z.int(), netCents: z.int().optional() })),
    rows: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        sublabel: z.string().nullable(),
        movementCount: z.int(),
        /** Menge in Tausendstel der Basiseinheit (Verlust negativ); null bei verschiedenen Einheiten */
        changeQty: z.int().nullable(),
        unit: z.string().nullable(),
        lossCents: z.int().optional(),
        gainCents: z.int().optional(),
        netCents: z.int().optional(),
      }),
    ),
    /** die Belege dahinter: Korrekturbuchungen und Inventuren, jüngste zuerst (höchstens 50) */
    documents: z.array(
      z.object({ id: zId, type: z.string(), typeLabel: z.string(), number: z.string().nullable(), date: zDay, locationCode: z.string(), reason: z.string().nullable(), movementCount: z.int(), netCents: z.int().optional() }),
    ),
    csv: zCsv.optional(),
  }),
  permission: READ_STOCK,
  risk: 'read',
  scope: reportScope,
  fieldPermissions: {
    ...costFields(['totals'], ['lossCents', 'gainCents', 'netCents', 'comparisonNetCents']),
    ...costFields(['rows.*'], ['lossCents', 'gainCents', 'netCents']),
    ...costFields(['sources.*', 'documents.*'], ['netCents']),
  },
})

export type StockDataState = z.output<typeof zDataState>
